import { useMemo } from "react";
import { Location } from "./location";
import { PartnerOffering } from "./partner_offering";
import {
  useLocationQueryMaybe,
  usePartnerOfferingImagesQuery,
} from "../offering/api";
import { Space } from "./space";

export interface ImageVersion {
  original: ImageData;
  thumb: ImageData;
  small: ImageData;
  medium: ImageData;
  large: ImageData;
  xlarge: ImageData;
}

export interface ImageData {
  url: string;
  width: number;
  height: number;
}

export function useStandardizeImages(
  offering: PartnerOffering,
  location?: Location,
  currentSpace?: Space,
  selectedSpaceID?: string,
  useLegacyImages?: boolean,
  legacyImages?: {
    imagesDesktop: ImageData[];
    imagesModal: ImageData[];
    imagesMobile: ImageData[];
  }
): {
  imagesDesktop: ImageData[];
  imagesModal: ImageData[];
  imagesMobile: ImageData[];
} {
  const firstPartnerOfferingLocation = offering.offeringLocations[0];
  const { data: partnerOfferingImages } = usePartnerOfferingImagesQuery(
    offering.id
  );
  const firstOfferingLocation = useLocationQueryMaybe(
    firstPartnerOfferingLocation?.location.id
  );

  const space = currentSpace;

  return useMemo(() => {
    // For Wework and Urbanplace
    if (useLegacyImages) {
      return (
        legacyImages || {
          imagesDesktop: [],
          imagesModal: [],
          imagesMobile: [],
        }
      );
    }

    // regardless of location selected partner wants the same offering level images
    // or when partner have more than one location and no selected location/space display offering images if existed
    if (
      partnerOfferingImages.length > 0 &&
      (offering.usePartnerOfferingImagesAcrossLocations ||
        (offering.offeringLocations.length > 1 &&
          !selectedSpaceID &&
          !location))
    ) {
      return {
        imagesDesktop: partnerOfferingImages.map((i: ImageVersion) => i.medium),
        imagesModal: partnerOfferingImages.map((i: ImageVersion) => i.large),
        imagesMobile: partnerOfferingImages.map((i: ImageVersion) => i.small),
      };
    }

    // 1. partner have only one location return the default location images
    // 2. When a location / space selected
    if (selectedSpaceID && space) {
      return {
        imagesDesktop: [
          ...space.images?.map((i) => i.medium),
          ...(location?.images?.map((i) => i.medium) || []),
        ],
        imagesModal: [
          ...space.images?.map((i) => i.large),
          ...(location?.images?.map((i) => i.large) || []),
        ],
        imagesMobile: [
          ...space.images?.map((i) => i.small),
          ...(location?.images?.map((i) => i.small) || []),
        ],
      };
    }

    // If no space, and location images exist, show location images
    if (!selectedSpaceID && location?.images) {
      return {
        imagesDesktop: location?.images?.map((i) => i.medium),
        imagesModal: location?.images?.map((i) => i.large),
        imagesMobile: location?.images?.map((i) => i.small),
      };
    }

    // Handle fallback - no location selected, no offering images -> display first location images and space.images
    return {
      imagesDesktop: [
        ...(space?.images?.map((i) => i.medium) || []),
        ...(firstOfferingLocation?.images?.map((i) => i.medium) || []),
      ],
      imagesModal: [
        ...(space?.images?.map((i) => i.large) || []),
        ...(firstOfferingLocation?.images?.map((i) => i.large) || []),
      ],
      imagesMobile: [
        ...(space?.images?.map((i) => i.small) || []),
        ...(firstOfferingLocation?.images?.map((i) => i.small) || []),
      ],
    };
  }, [
    useLegacyImages,
    partnerOfferingImages,
    offering.usePartnerOfferingImagesAcrossLocations,
    offering.offeringLocations.length,
    selectedSpaceID,
    location,
    space,
    firstOfferingLocation,
    legacyImages,
  ]);
}

export function useMembershipStandardizeImages(
  membershipImages: ImageVersion[],
  offering?: PartnerOffering
): {
  imagesDesktop: ImageData[];
  imagesModal: ImageData[];
  imagesMobile: ImageData[];
} {
  const { data: partnerOfferingImages } = usePartnerOfferingImagesQuery(
    offering?.id
  );

  if (membershipImages.length) {
    return {
      imagesDesktop: membershipImages.map((i: ImageVersion) => i.medium),
      imagesModal: membershipImages.map((i: ImageVersion) => i.large),
      imagesMobile: membershipImages.map((i: ImageVersion) => i.small),
    };
  }

  return {
    imagesDesktop: partnerOfferingImages.map((i: ImageVersion) => i.medium),
    imagesModal: partnerOfferingImages.map((i: ImageVersion) => i.large),
    imagesMobile: partnerOfferingImages.map((i: ImageVersion) => i.small),
  };
}
